"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { cn } from "@/lib/utils";
function useScrollState(threshold = 10) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > threshold);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [threshold]);
  return scrolled;
}
function useSmoothScroll() {
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
}
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-24 bg-muted animate-pulse rounded-md" data-index={i} />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative text-sm font-medium text-foreground/80 transition-colors hover:text-foreground py-2 block">
                {item.label}
                <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-ring transition-all duration-300 ease-out group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 p-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-full bg-muted animate-pulse rounded-md" data-index={i} />)}
      </div>;
  }
  return <nav className="flex flex-col p-6 space-y-2">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-lg font-medium text-foreground py-3 border-b border-border/40 hover:text-ring transition-colors" onClick={onClose} data-index={i}>
          {item.label}
        </Link>)}
      <div className="pt-6 mt-4">
        <Button className="w-full text-base py-6 bg-ring text-primary hover:bg-ring/90 rounded-none font-bold shadow-[0_0_20px_rgba(124,250,0,0.2)] border-none" size="lg" asChild>
          <Link to="/contact" onClick={onClose}>Request Evaluation</Link>
        </Button>
      </div>
    </nav>;
}
export default function Header() {
  const isScrolled = useScrollState(20);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useSmoothScroll();
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);
  return <header id="header" className="sticky top-0 z-50 w-full transition-all duration-300 border-b backdrop-blur-md border-border shadow-sm !bg-[hsl(0,0%,0%)] !border-none border-[rgb(51,51,51)]">
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0">
          <Link to="/" className="flex items-center">
            <img src="https://pumped-shrew.10web.cloud/wp-content/uploads/2026/03/crglogo-white.png" alt="Catalyst Resource Group, LLC" className="h-7" />
          </Link>
        </div>

        {}
        <div className="hidden lg:flex flex-1 justify-center">
          <MenuProvider menu_id="30" className="flex items-center">
            <DesktopMenu />
          </MenuProvider>
        </div>

        {}
        <div className="hidden lg:flex items-center gap-6">
          <Link to="tel:9512199312" className="text-sm font-medium text-muted-foreground hover:text-ring flex items-center gap-2 transition-colors">
            <Phone className="h-4 w-4" />
            <span>(951) 219-9312</span>
          </Link>
          <Button asChild className="bg-ring text-primary hover:bg-ring/90 font-bold transition-all duration-300 rounded-none shadow-[0_0_20px_rgba(124,250,0,0.2)] border-none">
            <Link to="/contact">Talk to an Expert</Link>
          </Button>
        </div>

        {}
        
        <div className="lg:hidden">
          <Button asChild className="bg-ring text-primary hover:bg-ring/90 font-bold transition-all duration-300 rounded-none shadow-[0_0_20px_rgba(124,250,0,0.2)] border-none">
            <Link to="/contact">Talk to an Expert</Link>
          </Button>
          {/*<Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu" aria-hidden="true" class="hidden">
            <Menu className="h-6 w-6" />
           </Button> */}
        </div>
        
          
      </div>

      {}
      {isMobileMenuOpen && <div className="fixed inset-0 z-50 bg-secondary lg:hidden flex flex-col animate-in slide-in-from-right duration-300">
          <div className="flex items-center justify-between p-4 border-b border-border/40 h-20">
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1567261/img/catalyst_resource_group_logo_updated.png" alt="Catalyst Resource Group, LLC" className="h-7" />
            </Link>
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)} aria-label="Close menu">
              <X className="h-6 w-6" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <MenuProvider menu_id="30">
              <MobileMenu onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
          </div>
          <div className="p-6 border-t bg-muted/20">
             <Link to="tel:9512199312" className="flex items-center justify-center gap-2 text-muted-foreground font-medium hover:text-ring transition-colors">
              <Phone className="h-4 w-4" />
              (951) 219-9312
            </Link>
          </div>
        </div>}
    </header>;
}